import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Plus, Bot, Edit2, Trash2, X, Save, Loader2, Search,
  MessageSquare, Zap, AlertCircle, CheckCircle, Tag, 
  Power, PowerOff, ChevronRight, ChevronDown, Image,
  Video, FileText, File, Paperclip
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CHATBOTS = 10;

// Trigger Types
const TRIGGER_TYPES = [
  { value: 'new_chat', label: 'New Chat Created', description: 'Triggers when a new WhatsApp conversation starts', icon: MessageSquare },
  { value: 'new_message', label: 'New Message Received', description: 'Triggers when any message is received', icon: MessageSquare },
  { value: 'keyword_match', label: 'Keyword Match', description: 'Triggers when message contains specific keywords', icon: Search },
  { value: 'label_applied', label: 'Label Applied', description: 'Triggers when a label is applied', icon: Tag },
  { value: 'custom', label: 'Custom Trigger', description: 'Custom trigger based on conditions', icon: Zap }
];

// Response Types
const RESPONSE_TYPES = [
  { value: 'text', label: 'Text Message', icon: MessageSquare },
  { value: 'image', label: 'Image', icon: Image },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'document', label: 'Document', icon: File },
  { value: 'template', label: 'Template Message', icon: FileText }
];

// Condition Operators
const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' }
];

// Status Badge
const StatusBadge = ({ status }) => {
  const isActive = status === 'Active';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
    }`}>
      {status}
    </span>
  );
};

// Chatbot Card
const ChatbotCard = ({ chatbot, onEdit, onDelete, onToggle }) => {
  const TriggerIcon = TRIGGER_TYPES.find(t => t.value === chatbot.trigger)?.icon || Zap;
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
      data-testid={`chatbot-card-${chatbot.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
            <Bot size={20} className="text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{chatbot.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={chatbot.status} />
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <TriggerIcon size={12} />
                {TRIGGER_TYPES.find(t => t.value === chatbot.trigger)?.label}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={() => onToggle(chatbot)}
          className={`p-2 rounded-lg transition-colors ${
            chatbot.status === 'Active' 
              ? 'text-green-600 hover:bg-green-50' 
              : 'text-gray-400 hover:bg-gray-100'
          }`}
          title={chatbot.status === 'Active' ? 'Deactivate' : 'Activate'}
        >
          {chatbot.status === 'Active' ? <Power size={18} /> : <PowerOff size={18} />}
        </button>
      </div>
      
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {chatbot.description || 'No description'}
      </p>
      
      {/* Keywords for keyword_match trigger */}
      {chatbot.trigger === 'keyword_match' && chatbot.triggerKeywords?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 mb-1">Keywords:</p>
          <div className="flex flex-wrap gap-1">
            {chatbot.triggerKeywords.slice(0, 5).map((kw, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                {kw}
              </span>
            ))}
            {chatbot.triggerKeywords.length > 5 && (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                +{chatbot.triggerKeywords.length - 5}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Stats */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>{chatbot.messagesCount || 0} messages sent</span>
          {chatbot.lastTriggeredAt && (
            <span>Last triggered: {new Date(chatbot.lastTriggeredAt).toLocaleDateString()}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(chatbot)}
            className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
            data-testid={`edit-chatbot-${chatbot.id}`}
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(chatbot)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
            data-testid={`delete-chatbot-${chatbot.id}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Chatbot Builder Modal (Periskope-style)
const ChatbotBuilderModal = ({ isOpen, onClose, onSave, chatbot, templates }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger: 'new_message',
    triggerKeywords: [],
    conditions: [],
    responseType: 'text',
    responseMessage: '',
    responseTemplateId: null,
    attachments: [],
    status: 'Active'
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  useEffect(() => {
    if (chatbot) {
      setFormData({
        name: chatbot.name || '',
        description: chatbot.description || '',
        trigger: chatbot.trigger || 'new_message',
        triggerKeywords: chatbot.triggerKeywords || [],
        conditions: chatbot.conditions || [],
        responseType: chatbot.responseType || 'text',
        responseMessage: chatbot.responseMessage || '',
        responseTemplateId: chatbot.responseTemplateId || null,
        attachments: chatbot.attachments || [],
        status: chatbot.status || 'Active'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        trigger: 'new_message',
        triggerKeywords: [],
        conditions: [],
        responseType: 'text',
        responseMessage: '',
        responseTemplateId: null,
        attachments: [],
        status: 'Active'
      });
    }
    setStep(1);
  }, [chatbot, isOpen]);
  
  const addKeyword = () => {
    if (keywordInput.trim() && !formData.triggerKeywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        triggerKeywords: [...prev.triggerKeywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };
  
  const removeKeyword = (kw) => {
    setFormData(prev => ({
      ...prev,
      triggerKeywords: prev.triggerKeywords.filter(k => k !== kw)
    }));
  };
  
  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'equals', value: '' }]
    }));
  };
  
  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };
  
  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };
  
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const uploadPromises = files.map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'chatbot-attachments');
        
        const response = await fetch(`${API_URL}/api/upload/file?folder=chatbot-attachments`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        
        if (!response.ok) throw new Error('Upload failed');
        return response.json();
      });
      
      const results = await Promise.all(uploadPromises);
      setFormData(prev => ({
        ...prev,
        attachments: [
          ...prev.attachments,
          ...results.map(r => ({
            fileUrl: r.url,
            fileName: r.filename,
            fileType: r.content_type
          }))
        ]
      }));
    } catch (error) {
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmit = async () => {
    if (!formData.name || !formData.description) {
      alert('Please fill in name and description');
      return;
    }
    
    if (!formData.responseMessage && !formData.responseTemplateId) {
      alert('Please provide a response message or select a template');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      alert(error.message || 'Failed to save chatbot');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl" data-testid="chatbot-builder-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Bot size={20} className="text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {chatbot ? 'Edit Chatbot' : 'Create New Chatbot'}
              </h2>
              <p className="text-sm text-gray-500">Build automated responses for WhatsApp</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        {/* Step Indicator */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-4">
          {['Basics', 'Trigger', 'Response'].map((label, idx) => (
            <button
              key={idx}
              onClick={() => setStep(idx + 1)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                step === idx + 1 
                  ? 'bg-teal-100 text-teal-700' 
                  : step > idx + 1 
                    ? 'text-teal-600' 
                    : 'text-gray-400'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                step === idx + 1 
                  ? 'bg-teal-600 text-white' 
                  : step > idx + 1 
                    ? 'bg-teal-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
              }`}>
                {step > idx + 1 ? <CheckCircle size={14} /> : idx + 1}
              </span>
              {label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Basics */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chatbot Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Welcome Bot, Sales Qualifier"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                  data-testid="chatbot-name-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description * (Mandatory)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this chatbot does..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                  data-testid="chatbot-description-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, status: 'Active' }))}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      formData.status === 'Active'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Power size={14} className="inline mr-2" />
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, status: 'Inactive' }))}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      formData.status === 'Inactive'
                        ? 'border-gray-500 bg-gray-50 text-gray-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <PowerOff size={14} className="inline mr-2" />
                    Inactive
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Trigger */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Trigger Type</label>
                <div className="grid grid-cols-1 gap-3">
                  {TRIGGER_TYPES.map((trigger) => {
                    const Icon = trigger.icon;
                    return (
                      <button
                        key={trigger.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, trigger: trigger.value }))}
                        className={`p-4 rounded-lg border text-left transition-all ${
                          formData.trigger === trigger.value
                            ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`trigger-${trigger.value}`}
                      >
                        <div className="flex items-start gap-3">
                          <Icon size={20} className={formData.trigger === trigger.value ? 'text-teal-600' : 'text-gray-400'} />
                          <div>
                            <p className="font-medium text-gray-800">{trigger.label}</p>
                            <p className="text-sm text-gray-500">{trigger.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Keywords for keyword_match trigger */}
              {formData.trigger === 'keyword_match' && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Keywords</label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                      placeholder="Enter keyword and press Enter"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={addKeyword}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.triggerKeywords.map((kw, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                        {kw}
                        <button onClick={() => removeKeyword(kw)} className="hover:text-blue-900">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Conditions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Additional Conditions (Optional)</label>
                  <button
                    type="button"
                    onClick={addCondition}
                    className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add Condition
                  </button>
                </div>
                
                {formData.conditions.length > 0 && (
                  <div className="space-y-3">
                    {formData.conditions.map((cond, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <input
                          type="text"
                          value={cond.field}
                          onChange={(e) => updateCondition(idx, 'field', e.target.value)}
                          placeholder="Field (e.g., city)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <select
                          value={cond.operator}
                          onChange={(e) => updateCondition(idx, 'operator', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {OPERATORS.map(op => (
                            <option key={op.value} value={op.value}>{op.label}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={cond.value}
                          onChange={(e) => updateCondition(idx, 'value', e.target.value)}
                          placeholder="Value"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          onClick={() => removeCondition(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 3: Response */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Response Type</label>
                <div className="flex flex-wrap gap-2">
                  {RESPONSE_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, responseType: type.value }))}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${
                          formData.responseType === type.value
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Icon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Template Selection */}
              {formData.responseType === 'template' && templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Template</label>
                  <div className="grid grid-cols-2 gap-2">
                    {templates.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          responseTemplateId: t.id,
                          responseMessage: t.message
                        }))}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          formData.responseTemplateId === t.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium text-gray-800 text-sm">{t.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">{t.message}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Response Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Message *
                </label>
                <div className="mb-2 flex flex-wrap gap-1">
                  <span className="text-xs text-gray-500">Variables:</span>
                  {['{hotelName}', '{ownerName}', '{phone}', '{city}'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, responseMessage: prev.responseMessage + v }))}
                      className="px-2 py-0.5 text-xs bg-teal-50 text-teal-700 rounded hover:bg-teal-100"
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <textarea
                  value={formData.responseMessage}
                  onChange={(e) => setFormData(prev => ({ ...prev, responseMessage: e.target.value }))}
                  placeholder="Type the automatic response message..."
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                  data-testid="response-message-input"
                />
              </div>
              
              {/* Attachments */}
              {['image', 'video', 'document'].includes(formData.responseType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept={formData.responseType === 'image' ? 'image/*' : formData.responseType === 'video' ? 'video/*' : '*'}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:border-teal-500 transition-colors"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                    {uploading ? 'Uploading...' : 'Add Attachment'}
                  </button>
                  
                  {formData.attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700">{att.fileName}</span>
                          <button onClick={() => removeAttachment(idx)} className="text-red-500 hover:text-red-700">
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Back
          </button>
          
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
              >
                Next
                <ChevronRight size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
                data-testid="save-chatbot-btn"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {chatbot ? 'Update Chatbot' : 'Create Chatbot'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, chatbot, loading }) => {
  if (!isOpen || !chatbot) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Trash2 size={32} className="text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Delete Chatbot</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <strong>"{chatbot.name}"</strong>? This action cannot be undone.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Chatbot Page
const WhatsAppChatbot = () => {
  const [chatbots, setChatbots] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchChatbots = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/chatbots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setChatbots(data.data || []);
    } catch (error) {
      console.error('Error fetching chatbots:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTemplates(data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchChatbots();
    fetchTemplates();
  }, [fetchChatbots, fetchTemplates]);
  
  const handleSaveChatbot = async (formData) => {
    const token = localStorage.getItem('token');
    const url = selectedChatbot 
      ? `${API_URL}/api/automation/chatbots/${selectedChatbot.id}`
      : `${API_URL}/api/automation/chatbots`;
    
    const response = await fetch(url, {
      method: selectedChatbot ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save chatbot');
    }
    
    await fetchChatbots();
    setSelectedChatbot(null);
  };
  
  const handleDeleteChatbot = async () => {
    if (!selectedChatbot) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/automation/chatbots/${selectedChatbot.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchChatbots();
      setDeleteModalOpen(false);
      setSelectedChatbot(null);
    } catch (error) {
      console.error('Error deleting chatbot:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleChatbot = async (chatbot) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/automation/chatbots/${chatbot.id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchChatbots();
    } catch (error) {
      console.error('Error toggling chatbot:', error);
    }
  };
  
  const filteredChatbots = chatbots.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <div className="space-y-6" data-testid="chatbot-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">WhatsApp Chatbots</h1>
          <p className="text-gray-500 mt-1">
            Create automated chatbots for WhatsApp conversations ({chatbots.length}/{MAX_CHATBOTS} used)
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedChatbot(null);
            setModalOpen(true);
          }}
          disabled={chatbots.length >= MAX_CHATBOTS}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-chatbot-btn"
        >
          <Plus size={18} />
          Create Chatbot
        </button>
      </div>
      
      {/* Limit Warning */}
      {chatbots.length >= MAX_CHATBOTS && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle size={20} className="text-amber-600" />
          <p className="text-amber-700 text-sm">
            You've reached the maximum limit of {MAX_CHATBOTS} chatbots. Delete an existing chatbot to create a new one.
          </p>
        </div>
      )}
      
      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search chatbots..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
        />
      </div>
      
      {/* Chatbots Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-teal-600" />
        </div>
      ) : filteredChatbots.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bot size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Chatbots Yet</h3>
          <p className="text-gray-500 mb-4">Create your first chatbot to automate WhatsApp conversations</p>
          <button
            onClick={() => {
              setSelectedChatbot(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            <Plus size={18} />
            Create Chatbot
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredChatbots.map((chatbot) => (
            <ChatbotCard
              key={chatbot.id}
              chatbot={chatbot}
              onEdit={(c) => {
                setSelectedChatbot(c);
                setModalOpen(true);
              }}
              onDelete={(c) => {
                setSelectedChatbot(c);
                setDeleteModalOpen(true);
              }}
              onToggle={handleToggleChatbot}
            />
          ))}
        </div>
      )}
      
      {/* Builder Modal */}
      <ChatbotBuilderModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedChatbot(null);
        }}
        onSave={handleSaveChatbot}
        chatbot={selectedChatbot}
        templates={templates}
      />
      
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedChatbot(null);
        }}
        onConfirm={handleDeleteChatbot}
        chatbot={selectedChatbot}
        loading={saving}
      />
    </div>
  );
};

export default WhatsAppChatbot;
